import React, { useReducer, createContext, useCallback, useEffect, useMemo } from 'react';
import { isUndefined } from 'lodash';
import { defaultState, reducers } from './reducers';
import { Actions, UseProgressState } from './types';
import { PageType } from '../GameProvider/types';
import useGame from '../../hooks/use-game';

export const ProgressContext = createContext<any>([]);

type ProgressProviderProps = {
  children: React.ReactNode;
  handlePageCompletion: (completed: boolean) => void;
};

const ProgressProvider: React.FC<ProgressProviderProps> = ({ children, handlePageCompletion }) => {
  const [{ currentData }] = useGame<PageType>();

  const [state, dispatch] = useReducer<React.Reducer<UseProgressState, Actions>>(
    reducers,
    defaultState()
  );

  const [currentTotal, currentFound] = useMemo(
    () =>
      currentData.reduce(
        (acc: [number | undefined, number], curr) => {
          const accTotal = isUndefined(acc[0])
            ? state.pages[curr.page?.pageNumber]?.total
            : acc[0] + (state.pages[curr.page?.pageNumber]?.total ?? 0);
          const accFound = acc[1] + (state.pages[curr.page?.pageNumber]?.found ?? 0);
          return [accTotal, accFound];
        },
        [undefined, 0]
      ),
    [currentData, state.pages]
  );

  const setTotal = useCallback(
    (page: number, total: number) => {
      dispatch({ type: 'SET_TOTAL', payload: { page, total } });
    },
    [state, dispatch]
  );

  const setFound = useCallback(
    (page: number) => {
      dispatch({ type: 'SET_FOUND', payload: { page } });
    },
    [state, dispatch]
  );

  useEffect(() => {
    const currentPageCompleted = !isUndefined(currentTotal) && currentFound === currentTotal;
    handlePageCompletion(currentPageCompleted);
  }, [currentTotal, currentFound]);

  return (
    <ProgressContext.Provider
      value={[
        {
          ...state,
          currentTotal,
          currentFound,
        },
        {
          setTotal,
          setFound,
        },
      ]}
    >
      {children}
    </ProgressContext.Provider>
  );
};

export default ProgressProvider;
